import { openDB } from 'idb';
import { reportClientEvent } from '../../lib/clientLog';

const STORES = ['completion-queue', 'stage-drafts', 'stage-outbox'];
const PRINCIPAL = 'installer_pwa_principal';
const BOUND = 'installer_pwa_bound_token';
const SESSION = 'installer_session_token';
let _dbPromise = null;
let generation = 0;
let logoutGeneration = 0;
let identityChange = Promise.resolve();
const flushes = new Map();
// Outbox lifecycle reports carry ids and the failure's status/code/request id only.
const outboxEvent = (name, item, e, extra = {}) => reportClientEvent(name, {
  client_attempt_key: item.client_attempt_key, job_id: item.jobId, job_stage_id: item.jobStageId,
  status: e?.status, code: e?.code, rid: e?.requestId, ...extra,
});

export function db() {
  if (!_dbPromise) {
    _dbPromise = openDB('installer-pwa', 2, {
      upgrade(d) {
        // Keep the v1 store and its records intact during the upgrade.
        if (!d.objectStoreNames.contains(STORES[0])) d.createObjectStore(STORES[0], { keyPath: 'jobId' });
        if (!d.objectStoreNames.contains(STORES[1])) d.createObjectStore(STORES[1], { keyPath: 'id' });
        if (!d.objectStoreNames.contains(STORES[2])) d.createObjectStore(STORES[2], { keyPath: 'client_attempt_key' });
      },
    });
  }
  return _dbPromise;
}
export async function closeDB() {
  if (_dbPromise) { (await _dbPromise).close(); _dbPromise = null; }
}
async function clearPrivateData() {
  if (typeof caches !== 'undefined') await caches.delete('installer-api');
  const d = await db();
  const tx = d.transaction(STORES, 'readwrite');
  await Promise.all(STORES.map((store) => tx.objectStore(store).clear()));
  await tx.done;
}
export function clearInstallerData() {
  logoutGeneration += 1;
  generation += 1;
  localStorage.removeItem(BOUND);
  localStorage.removeItem(PRINCIPAL);
  identityChange = identityChange.catch(() => {}).then(clearPrivateData);
  return identityChange;
}
export function bindInstaller(installerId, token) {
  if (!installerId || !token) return Promise.reject(new Error('نشست نیروی اجرایی معتبر نیست. دوباره وارد شوید.'));
  const expectedLogout = logoutGeneration;
  const valid = () => logoutGeneration === expectedLogout && localStorage.getItem(SESSION) === token;
  identityChange = identityChange.catch(() => {}).then(async () => {
    if (!valid()) throw new Error('نشست نیروی اجرایی تغییر کرد. دوباره وارد شوید.');
    if (localStorage.getItem(PRINCIPAL) !== installerId) {
      generation += 1;
      localStorage.removeItem(BOUND);
      await clearPrivateData();
      if (!valid()) throw new Error('نشست نیروی اجرایی تغییر کرد. دوباره وارد شوید.');
      localStorage.setItem(PRINCIPAL, installerId);
    }
    // Persist the token/id binding for offline reloads; the shared session still owns it.
    localStorage.setItem(BOUND, token);
    return installerId;
  });
  return identityChange;
}
export function currentInstaller(token) {
  return token && token === localStorage.getItem(SESSION) && token === localStorage.getItem(BOUND)
    ? localStorage.getItem(PRINCIPAL) : null;
}
export async function requestQueueSync() {
  if ('serviceWorker' in navigator && 'SyncManager' in window) {
    try { const reg = await navigator.serviceWorker.ready; await reg.sync.register('installer-flush-queue'); } catch { /* Best effort. */ }
  }
}
export async function enqueueCompletion({ jobId, installerId, photos, note }) {
  await identityChange;
  const d = await db();
  if (localStorage.getItem(PRINCIPAL) !== installerId) throw new Error('نشست نیروی اجرایی تغییر کرد. دوباره وارد شوید.');
  await d.put(STORES[0], { jobId: `${installerId}:${jobId}`, actualJobId: jobId, installer_id: installerId, photos, note, queuedAt: Date.now() });
  await requestQueueSync();
}
export async function listQueued(installerId = localStorage.getItem(PRINCIPAL)) {
  if (!installerId) return [];
  const items = await (await db()).getAll(STORES[0]);
  return items.filter((i) => (i.installer_id || i.installerId) === installerId)
    .map((i) => ({ ...i, queue_key: i.jobId, jobId: i.actualJobId || i.jobId }));
}
export async function isQueued(jobId, installerId) { return (await listQueued(installerId)).some((i) => i.jobId === jobId); }
export async function dequeue(key) {
  const d = await db();
  for (const item of (await listQueued()).filter((i) => i.queue_key === key || i.jobId === key)) await d.delete(STORES[0], item.queue_key);
}
function singleFlush(key, run) {
  if (!flushes.has(key)) flushes.set(key, run().finally(() => flushes.delete(key)));
  return flushes.get(key);
}
function sessionGuard(installerId, token) {
  const epoch = generation;
  return () => epoch === generation && currentInstaller(token) === installerId;
}
export function flushQueue({ token, installerId, uploadViaSignedUrl, completeJob }) {
  return singleFlush(`completion:${installerId}:${token}`, async () => {
    await identityChange;
    const valid = sessionGuard(installerId, token);
    if (!installerId || !valid()) return [];
    const results = [];
    for (const item of await listQueued(installerId)) {
      if (!valid()) break;
      try {
        const photos = item.photos || (item.photoBlob ? [{ type: 'cabling', blob: item.photoBlob }] : []);
        const uploaded = [];
        for (const ph of photos) {
          if (!valid()) return results;
          const path = await uploadViaSignedUrl('job_photo', ph.blob, { session_token: token }, 'image');
          uploaded.push({ type: ph.type, path });
        }
        if (!valid()) return results;
        await completeJob(token, item.jobId, uploaded, item.note);
        if (!valid()) return results;
        await (await db()).delete(STORES[0], item.queue_key);
        results.push({ jobId: item.jobId, ok: true });
      } catch (e) {
        if (!valid()) return results;
        // A storage 409 on the photo upload is not the server's "already completed".
        if (e.status === 409 && e.code !== 'upload_failed') {
          await (await db()).delete(STORES[0], item.queue_key);
          outboxEvent('outbox.dropped_conflict', item, e);
        }
        results.push({ jobId: item.jobId, ok: false, error: e.message });
      }
    }
    return results;
  });
}
export async function enqueueStage(draft) {
  await identityChange;
  if (draft.bundleItemResults) throw new Error('برای ثبت نتیجه اقلام پکیج باید آنلاین باشید.');
  const d = await db();
  if (localStorage.getItem(PRINCIPAL) !== draft.installer_id) throw new Error('نشست نیروی اجرایی تغییر کرد. دوباره وارد شوید.');
  const tx = d.transaction(STORES[2], 'readwrite');
  const previous = await tx.store.get(draft.client_attempt_key);
  // Only this attempt's photos keep their tickets and uploads after a refusal; a photo id is its client_key.
  const tickets = new Map(previous?.id === draft.id && previous.installer_id === draft.installer_id
    ? previous.photos.filter((photo) => photo.id && photo.ticket_id)
      .map((photo) => [photo.id, { ticket_id: photo.ticket_id, uploaded: Boolean(photo.uploaded), state: photo.state }]) : []);
  const photos = draft.photos.map((photo) => ({ ...photo, ...tickets.get(photo.id) }));
  await tx.store.put({ ...draft, photos, queued_at: Date.now(), error: null, retry_at: null });
  await tx.done;
  await requestQueueSync();
}
export async function listStageOutbox(installerId) {
  return (await (await db()).getAll(STORES[2])).filter((i) => i.installer_id === installerId);
}
// ponytail: a photo not committed within this window after its shutter is an offline
// capture (review P1-2b); widen it if slow links flag honest online shots.
export const COMMIT_WINDOW_MS = 60000;
export const TOO_MANY_PHOTOS_MESSAGE = 'سقف عکس این مرحله پر شده است؛ با عکس‌های فعلی ارسال کنید یا با دفتر تماس بگیرید.';
const RETAKE_CODES = ['DUPLICATE_PHOTO', 'TICKET_KEY_REUSED', 'OFFLINE_NOT_ALLOWED', 'TOO_MANY_PHOTOS', 'EVIDENCE_INVALID'];
// A commit refusal that only a new shot can resolve: its message, else null.
export const commitRefusal = (e) => (RETAKE_CODES.includes(e?.code) ? (e.code === 'TOO_MANY_PHOTOS' ? TOO_MANY_PHOTOS_MESSAGE : e.message) : null);
const slotNames = (photos) => [...new Set(photos.map((p) => `«${p.slot_label || p.kind}»`))].join(' و ');

// The capture commit (contract C2) of one stored photo; the page and the flush share it.
export function commitPhoto(photo, { token, jobId, jobStageId, captureTicket, offline = false, timeoutMs = 0 }) {
  const request = captureTicket(token, jobId, jobStageId, { client_key: photo.id, kind: photo.kind, sha256: photo.sha256,
    lat: photo.lat ?? null, lng: photo.lng ?? null, accuracy_m: photo.accuracy_m ?? null, fix_at: photo.fix_at ?? null,
    camera: photo.camera ?? {}, offline, device_captured_at: photo.device_captured_at ?? null });
  if (!timeoutMs) return request;
  // A late answer is harmless: a retry with the same client_key gets the same ticket.
  let timer;
  const timeout = new Promise((resolve, reject) => { timer = setTimeout(() => reject(new TypeError('commit timed out')), timeoutMs); });
  return Promise.race([request, timeout]).finally(() => clearTimeout(timer));
}

const sessionChanged = () => new Error('نشست نیروی اجرایی تغییر کرد. دوباره وارد شوید.');
// Commit what is not committed (offline once past the window), upload what is not
// uploaded, then post ticket ids only. A refusal that needs a retake carries
// e.retake = { ids, message } naming the slots.
export async function sendStageAttempt(item, { token, captureTicket, captureUploadUrl, putSigned, submitStageAttempt, save, onProgress, valid = () => true }) {
  const guard = () => { if (!valid()) throw sessionChanged(); };
  for (let i = 0; i < item.photos.length; i += 1) {
    guard();
    const ph = item.photos[i];
    if (onProgress) onProgress(i + 1, item.photos.length);
    if (!ph.ticket_id) {
      const offline = ph.state === 'offline' || Date.now() - ph.shutter_at > COMMIT_WINDOW_MS;
      let ticket;
      try { ticket = await commitPhoto(ph, { token, jobId: item.jobId, jobStageId: item.jobStageId, captureTicket, offline }); } catch (e) {
        const refused = commitRefusal(e);
        if (refused) e.retake = { ids: [ph.id], message: `عکس ${slotNames([ph])}: ${refused}` };
        throw e;
      }
      Object.assign(ph, { ticket_id: ticket.ticket_id, state: offline ? 'offline' : 'committed' });
      guard(); await save(item);
    }
    if (!ph.uploaded) {
      // Asked right before each PUT, so a URL never expires in the queue.
      const target = await captureUploadUrl(token, ph.ticket_id);
      guard();
      if (!target.uploaded) await putSigned(target.signed_url, ph.blob);
      ph.uploaded = true;
      guard(); await save(item);
    }
  }
  guard();
  try {
    return await submitStageAttempt(token, item.jobId, item.jobStageId, {
      client_attempt_key: item.client_attempt_key, client_submitted_at: item.client_submitted_at,
      note: item.note || null, field_values: item.field_values || {}, field_warning_acknowledgements: item.field_warning_acknowledgements || [], photos: item.photos.map((ph) => ({ ticket_id: ph.ticket_id })),
      ...(item.bundleItemResults ? { bundleItemResults: item.bundleItemResults } : {}),
    });
  } catch (e) {
    const refused = e.code === 'TICKET_INVALID' ? item.photos.filter((ph) => (e.ticket_ids || []).includes(ph.ticket_id)) : [];
    if (refused.length) e.retake = { ids: refused.map((ph) => ph.id), message: `عکس ${slotNames(refused)} پذیرفته نشد؛ دوباره بگیرید.` };
    throw e;
  }
}
export function flushStageOutbox({ token, installerId, captureTicket, captureUploadUrl, putSigned, submitStageAttempt, fetchJob, onProgress }) {
  return singleFlush(`stage:${installerId}:${token}`, async () => {
    await identityChange;
    const valid = sessionGuard(installerId, token);
    if (!installerId || !valid()) return [];
    const d = await db();
    const results = [];
    const drop = async (item) => {
      const tx = d.transaction([STORES[1], STORES[2]], 'readwrite');
      await tx.objectStore(STORES[2]).delete(item.client_attempt_key);
      const drafts = tx.objectStore(STORES[1]);
      const draft = await drafts.get(item.id);
      if (draft?.client_attempt_key === item.client_attempt_key) await drafts.delete(item.id);
      await tx.done;
    };
    // One transaction drops exactly the refused photos from the outbox item and its draft.
    const retake = async (item, { ids, message }, e) => {
      const keep = (photo) => !ids.includes(photo.id);
      const tx = d.transaction([STORES[1], STORES[2]], 'readwrite');
      await tx.objectStore(STORES[2]).put({ ...item, photos: item.photos.filter(keep), error: message,
        status: e.status ?? null, code: e.code ?? null, rid: e.requestId ?? null });
      const drafts = tx.objectStore(STORES[1]);
      const draft = await drafts.get(item.id);
      if (draft?.client_attempt_key === item.client_attempt_key) await drafts.put({ ...draft, photos: draft.photos.filter(keep) });
      await tx.done;
    };
    for (const item of await listStageOutbox(installerId)) {
      if (!valid()) break;
      if (item.error || item.retry_at > Date.now()) continue; // Refusals need correction; throttles need time.
      const identity = { jobId: item.jobId, jobStageId: item.jobStageId, client_attempt_key: item.client_attempt_key };
      try {
        await sendStageAttempt(item, { token, captureTicket, captureUploadUrl, putSigned, submitStageAttempt, valid, onProgress,
          save: (record) => d.put(STORES[2], record) });
        if (!valid()) break;
        await drop(item);
        results.push({ ...identity, ok: true });
      } catch (e) {
        if (!valid()) break;
        if (e.status === 409 && /stale_state|job_not_active/i.test(e.code || '')) {
          try {
            const response = await fetchJob(token, item.jobId);
            if (!valid()) break;
            const stage = response.job?.process?.stages?.find((s) => s.id === item.jobStageId);
            if (stage && !['open', 'failed'].includes(stage.state)) {
              await drop(item);
              outboxEvent('outbox.dropped_stale', item, e);
              results.push({ ...identity, ok: false, dropped: true, error: e.message });
              continue;
            }
          } catch { /* Retain evidence unless a refresh proves the stage moved. */ }
        }
        if (!valid()) break;
        if (e.retake) {
          await retake(item, e.retake, e);
          outboxEvent('outbox.retake', item, e, { count: e.retake.ids.length });
          results.push({ ...identity, ok: false, error: e.retake.message });
          continue;
        }
        if (e.status === 429) {
          item.retry_at = Date.now() + 60000;
          await d.put(STORES[2], item);
        } else if (e.status >= 400 && e.status < 500) {
          Object.assign(item, { error: e.message, status: e.status, code: e.code ?? null, rid: e.requestId ?? null });
          await d.put(STORES[2], item);
          outboxEvent('outbox.refused', item, e);
        }
        results.push({ ...identity, ok: false, error: e.message });
      }
    }
    return results;
  });
}
export async function pruneStageData(installerId, now = Date.now()) {
  const d = await db();
  const ids = new Set();
  for (const store of STORES.slice(1)) {
    for (const item of await d.getAll(store)) {
      if (item.installer_id === installerId && (item.queued_at || item.updated_at) < now - 7 * 86400000) {
        await d.delete(store, store === STORES[1] ? item.id : item.client_attempt_key);
        ids.add(item.id);
      }
    }
  }
  if (ids.size) reportClientEvent('outbox.pruned', { count: ids.size });
  return ids.size;
}
export function isNetworkError(e) {
  return e instanceof TypeError || /failed to fetch|load failed|networkerror|network request failed/i.test(e.message || '');
}
